import React, { useEffect, useState, useRef } from 'react';
import styles from '../../styles/AuditLogModal.module.css';
import { Button } from '../../components/Modal';
import AuditLogTimeline from '../../components/TimeLine';
import SkeletonAuditLogTimeline from '../../Skeletons/AuditLogSkeleton';
import SelectInputBox from '../../components/Modal/SelectInputBox';
import { InputField } from '../../components/Modal';
import { useFetchAuditLog, useFetchActionType } from '../../hooks/patients';
import { IPatient, IAuditLog } from '../../hooks/patients/index.type';

import { useDebounce } from 'use-debounce';
import { formatDateToMMDDYYYY, subtractDays, formatDateForInput } from '../../utils/auditDates';

interface Props {
  show: boolean;
  patient: IPatient;
  onClose: () => void;
}

const AuditLogsModal: React.FC<Props> = ({ onClose, patient }) => {
  const [resetFlag, setResetFlag] = useState(false);
  const today = new Date();
  const todayStr = formatDateForInput(today);
  const WEEK_DAYS = 7;
  const MAX_RANGE_DAYS = 90;

  const [rawFromDate, setRawFromDate] = useState(formatDateForInput(subtractDays(today, 6)));
  const [rawToDate, setRawToDate] = useState(todayStr);

  const [fromDateError, setFromDateError] = useState('');
  const [toDateError, setToDateError] = useState('');

  const [currentStartDate, setCurrentStartDate] = useState(subtractDays(today, 6));
  const [actionData, setActionData] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const [debouncedFromDate] = useDebounce(rawFromDate, 3000);
  const [debouncedToDate] = useDebounce(rawToDate, 3000);

  const { getAuditLog, auditLog } = useFetchAuditLog();
  const { fetchActionType, actionType } = useFetchActionType();

  const [allLogs, setAllLogs] = useState<IAuditLog[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isDayExpanded, setIsDayExpanded] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchActionType();
  }, []);

  const handleFromDateChange = (value: string) => {
    const newFromDate = new Date(value);
    const currentToDate = new Date(rawToDate);

    let error = '';

    if (newFromDate > today) {
      error = 'From date cannot be in the future.';
      setFromDateError(error);
      setRawFromDate(todayStr);
      return;
    }

    if (newFromDate > currentToDate) {
      setRawToDate(value);
    }

    const diffDays = (currentToDate.getTime() - newFromDate.getTime()) / (1000 * 3600 * 24);
    if (diffDays > MAX_RANGE_DAYS) {
      const newToDate = new Date(newFromDate);
      newToDate.setDate(newToDate.getDate() + MAX_RANGE_DAYS);
      setRawToDate(formatDateForInput(newToDate));
    }

    setRawFromDate(value);
    setFromDateError('');
  };

  const handleToDateChange = (value: string) => {
    const newToDate = new Date(value);
    const currentFromDate = new Date(rawFromDate);

    let error = '';

    if (newToDate > today) {
      error = 'To date cannot be in the future.';
      setToDateError(error);
      setRawToDate(todayStr);
      return;
    }

    if (newToDate < currentFromDate) {
      setRawFromDate(value);
    }

    const diffDays = (newToDate.getTime() - currentFromDate.getTime()) / (1000 * 3600 * 24);
    if (diffDays > MAX_RANGE_DAYS) {
      const newFromDate = new Date(newToDate);
      newFromDate.setDate(newFromDate.getDate() - MAX_RANGE_DAYS);
      setRawFromDate(formatDateForInput(newFromDate));
    }

    setRawToDate(value);
    setToDateError('');
  };

  const fetchLogsForWeek = async (from: Date, to: Date, replace = false) => {
    if ((!hasMore && !replace) || loading || loadingMore) return;

    const el = scrollRef.current;
    const prevScrollHeight = el?.scrollHeight ?? 0;
    const prevScrollTop = el?.scrollTop ?? 0;

    if (replace) {
      setLoadingMore(false);
      setLoading(true);
    } else {
      setLoading(false);
      setLoadingMore(true);
    }

    const fromStr = formatDateToMMDDYYYY(from);
    const toStr = formatDateToMMDDYYYY(to);

    try {
      const newLogs = await getAuditLog({
        patientId: patient._id ?? '',
        fromDate: fromStr,
        toDate: toStr,
        action: actionData || '',
        search: searchTerm || '',
      });

      if (!newLogs || newLogs.length === 0) {
        if (replace) setAllLogs([]);
        setHasMore(false);
      } else {
        if (replace) {
          setAllLogs(newLogs);
        } else {
          setAllLogs(prev => [...prev, ...newLogs]);
        }

        if (newLogs.length < WEEK_DAYS) {
          setHasMore(false);
        }
      }

      if (el && !replace) {
        requestAnimationFrame(() => {
          const newScrollHeight = el.scrollHeight;
          el.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
        });
      }
    } catch (error) {
      console.error(error);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogsForWeek(subtractDays(today, 6), today, true);
  }, []);
  useEffect(() => {
    const from = new Date(rawFromDate);
    const to = new Date(rawToDate);

    const filtersActive = debouncedSearchTerm || actionData || rawFromDate !== formatDateForInput(subtractDays(today, 6)) || rawToDate !== todayStr;

    if (filtersActive) {
      scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      setCurrentStartDate(from);
      setHasMore(true);
      setIsFilterApplied(true);
      fetchLogsForWeek(from, to, true);
    }
  }, [debouncedSearchTerm]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || loading || loadingMore || !hasMore || isFilterApplied || isDayExpanded) return;

    const scrollPosition = el.scrollTop + el.clientHeight;
    const scrollHeight = el.scrollHeight;

    if (scrollHeight - scrollPosition < 100) {
      const newToDate = subtractDays(currentStartDate, 1);
      const newFromDate = subtractDays(newToDate, WEEK_DAYS - 1);
      fetchLogsForWeek(newFromDate, newToDate);
      setCurrentStartDate(newFromDate);
    }
  };

  const handleApplyFilter = () => {
    const from = new Date(rawFromDate);
    const to = new Date(rawToDate);

    if (from > to) {
      setFromDateError('From date cannot be after To date');
      return;
    } else {
      setFromDateError('');
    }


    setCurrentStartDate(from);
    setHasMore(true);

    const filtersActive = searchTerm || actionData || rawFromDate !== formatDateForInput(subtractDays(today, 6)) || rawToDate !== todayStr;
    setIsFilterApplied(!!filtersActive);

    scrollRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    fetchLogsForWeek(from, to, true);
  };

  const handleResetFilters = () => {
    const defaultFrom = formatDateForInput(subtractDays(today, 6));
    const defaultTo = todayStr;

    setSearchTerm('');
    setActionData('');
    setRawFromDate(defaultFrom);
    setRawToDate(defaultTo);
    setFromDateError('');
    setToDateError('');
    setIsFilterApplied(false);
    setCurrentStartDate(subtractDays(today, 6));
   
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });

  
  };

  useEffect(() => {
    const defaultFrom = formatDateForInput(subtractDays(today, 6));
    const defaultTo = todayStr;
    if (
      searchTerm === '' &&
      actionData === '' &&
      rawFromDate === defaultFrom &&
      rawToDate === defaultTo &&
      !isFilterApplied
    ) {
      fetchLogsForWeek(subtractDays(today, 6), today, true);
    }

  }, [searchTerm, actionData, rawFromDate, rawToDate, isFilterApplied]);

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>Audit Logs</h2>
          <button onClick={onClose} className={styles.closeButton} aria-label="close">
            <svg className={styles.closeButtonIcon}>
              <use xlinkHref={'/assets/sprite.svg#icon-close-cross'} />
            </svg>
          </button>
        </div>

        <div className={styles.filters}>
          <div className={styles.header_text_input}>
            <svg className={styles.header_text_input_icon}>
              <use xlinkHref={'/assets/sprite.svg#icon-search-new'} />
            </svg>
            <input type='text' className={styles.header_text_box} placeholder='Search audit logs...' value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>

          <div className={styles.filterRow}>
            <SelectInputBox
              value={actionData}
              onChange={e => setActionData(e.target.value)}
              label='Action Type'
              containerClass={styles.select}
              customClass={styles.select_custom}
              labelClass={styles.label}
            >
              <option value=''>Select audit action type</option>
              {actionType.map((action, index) => (
                <option key={index} value={action}>
                  {action}
                </option>
              ))}
            </SelectInputBox>

            <InputField
              label='From Date'
              type='date'
              placeholder='YYYY-MM-DD'
              value={rawFromDate}
              onChange={e => handleFromDateChange(e.target.value)}
              max={rawToDate > todayStr ? todayStr : rawToDate}
              containerClass={styles.select}
              labelClass={styles.label}
              errorMessage={fromDateError}
            />

            <InputField
              label='To Date'
              type='date'
              placeholder='YYYY-MM-DD'
              value={rawToDate}
              onChange={e => handleToDateChange(e.target.value)}
              max={todayStr}
              containerClass={styles.select}
              labelClass={styles.label}
              errorMessage={toDateError}
            />

            <Button
              type='button'
              label='Filter'
              onClick={handleApplyFilter}
              submitButtonStyle={{
                height: '4.25rem',
                marginTop: '2.75rem',
                borderRadius: '0.8rem',
              }}
              span={false}
            />
            <Button type='button' label='Reset' customClass={styles.resetButton} onClick={handleResetFilters} span={false} />
          </div>
        </div>

        <div className={styles.contentContainer} ref={scrollRef} onScroll={handleScroll} style={{ height: '75rem', overflowY: 'auto', marginBottom: '0.5rem' }}>
          {loading ? (
            <SkeletonAuditLogTimeline />
          ) : allLogs.length > 0 ? (
            <>
              <AuditLogTimeline data={allLogs} setIsDayExpanded={setIsDayExpanded} />
              {loadingMore && <SkeletonAuditLogTimeline />}
            </>
          ) : (
            <p style={{ textAlign: 'center', marginTop: '2rem' }}>No logs found</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLogsModal;
